<template>
  <ModalAiChatWindow
    :id="id"
    ref="chatWindowRef"
    :modalClass="`custom-modal_no-padding modal-ai-chat${isExpanded ? '' : ' modal-ai-chat--compact'}${pipContainer || isPipOpening || isClosePending ? ' modal-ai-chat--detached' : ''}`"
    draggable=".custom-modal__header"
    :preventClose="isSending"
    :resizable="!isExpanded"
    :minWidth="minWindowSize.width"
    :minHeight="minWindowSize.height"
    @beforeOpen="onOpened"
    @opened="focusInput"
    @closed="onClosed"
  >
    <template #header="{ hide }">
      <Teleport
        :to="pipContainer || 'body'"
        :disabled="!pipContainer"
      >
        <div class="modal-ai-chat__header">
          <AIResponseAgent
            :img="aiProfile.picture.pictureUrl"
            :name="aiProfile.name"
            :addAvatarBorder="true"
            class="modal-ai-chat__user-info"
          >
            <template #agent-name>
              <BaseHeading
                level="3"
                class="modal-ai-chat__title"
              >
                Ask {{ aiProfile.name || "AI agent" }}, your AI Assistant
              </BaseHeading>
            </template>
          </AIResponseAgent>
          <div class="modal-ai-chat__header-actions">
            <button
              v-if="pipContainer"
              type="button"
              class="modal-ai-chat__header-action"
              aria-label="Return chat to the page"
              @click="closePipToPage"
            >
              <icon
                data="@icon/chat-pop-in.svg"
                width="var(--icon-24)"
                height="var(--icon-24)"
              />
            </button>
            <template v-else>
              <button
                type="button"
                class="modal-ai-chat__header-action"
                :aria-label="isExpanded ? 'Use compact chat' : 'Expand chat'"
                @click="onToggleSize"
              >
                <icon
                  v-if="isExpanded"
                  data="@icon/chat-resize.svg"
                  width="var(--icon-24)"
                  height="var(--icon-24)"
                />
                <icon
                  v-else
                  data="@icon/chat-expand.svg"
                  width="var(--icon-24)"
                  height="var(--icon-24)"
                />
              </button>
              <button
                v-if="isPipSupported"
                type="button"
                class="modal-ai-chat__header-action"
                aria-label="Open chat in picture-in-picture"
                @click="openPip"
              >
                <icon
                  data="@icon/chat-pop-out.svg"
                  width="var(--icon-24)"
                  height="var(--icon-24)"
                />
              </button>
              <button
                type="button"
                class="modal-ai-chat__header-action"
                :disabled="isSending"
                @click="hide()"
              >
                <icon
                  data="@icon/chat-close.svg"
                  width="var(--icon-24)"
                  height="var(--icon-24)"
                />
              </button>
            </template>
          </div>
        </div>
      </Teleport>
    </template>

    <template #body>
      <Teleport
        :to="pipContainer || 'body'"
        :disabled="!pipContainer"
      >
        <div class="modal-ai-chat__body">
          <div
            class="modal-ai-chat__sidebar"
            :class="{ 'modal-ai-chat__sidebar--collapsed': !isSidebarOpen }"
          >
            <button
              v-if="!isExpanded || pipContainer"
              type="button"
              class="common-gray-interactive modal-ai-chat__sidebar-toggle"
              :aria-label="isSidebarOpen ? 'Hide suggested questions' : 'Show suggested questions'"
              :aria-expanded="isSidebarOpen"
              @click="isSidebarOpen = !isSidebarOpen"
            >
              <icon
                v-if="isSidebarOpen"
                data="@icon/chat-sidebar-close.svg"
                width="var(--icon-24)"
                height="var(--icon-24)"
              />
              <icon
                v-else
                data="@icon/chat-sidebar-open.svg"
                width="var(--icon-24)"
                height="var(--icon-24)"
              />
            </button>
            <div
              v-if="isSidebarOpen && suggestedQuestions.length"
              class="modal-ai-chat__suggested"
            >
              <BaseSubHeading
                class="modal-ai-chat__suggested-title"
                size="medium"
                weight="600"
                color="monochrome-06"
              >
                Suggested
              </BaseSubHeading>
              <ul class="modal-ai-chat__question-list">
                <li
                  v-for="(question, index) in suggestedQuestions"
                  :key="index"
                  class="modal-ai-chat__question-item"
                  @click="onSelectQuestion(question)"
                >
                  <icon
                    data="@icon/suggested.svg"
                    :fill="false"
                    width="var(--icon-16)"
                    height="var(--icon-16)"
                    color="var(--monochrome05to04)"
                    class="modal-ai-chat__question-icon"
                  />
                  <span class="modal-ai-chat__question-text">{{ question }}</span>
                </li>
              </ul>
            </div>
            <div
              v-if="isSidebarOpen && aiProvider"
              class="modal-ai-chat__powered-by"
            >
              <span class="modal-ai-chat__powered-by-label">Powered by</span>
              <a
                class="link is-small-400 modal-ai-chat__powered-by-link"
                :href="aiProvider.url"
                target="_blank"
              >
                {{ aiProvider.name }}
              </a>
            </div>
          </div>

          <div class="modal-ai-chat__chat">
            <div class="modal-ai-chat__conversation">
              <div
                v-if="activeConfirmAction === 'resetContext'"
                class="modal-ai-chat__confirm-action"
              >
                <BaseHeading
                  level="3"
                  class="modal-ai-chat__confirm-action__title"
                >
                  Reset conversation context?
                </BaseHeading>
                <BaseSubHeading
                  size="medium"
                  weight="400"
                  class="modal-ai-chat__confirm-action__text"
                >
                  This will clear AI memory of previous messages and responses.
                </BaseSubHeading>
                <ControlButtons
                  class="modal-ai-chat__confirm-action__buttons"
                  @cancel="closeConfirmAction"
                  @save="onResetContext()"
                >
                  <template #save> Reset Context </template>
                </ControlButtons>
              </div>
              <div
                v-else-if="activeConfirmAction === 'clearChatHistory'"
                class="modal-ai-chat__confirm-action"
              >
                <BaseHeading
                  level="3"
                  class="modal-ai-chat__confirm-action__title"
                >
                  Clear Chat History?
                </BaseHeading>
                <BaseSubHeading
                  size="medium"
                  weight="400"
                  class="modal-ai-chat__confirm-action__text"
                >
                  This will permanently delete all messages in this chat.
                </BaseSubHeading>
                <ControlButtons
                  class="modal-ai-chat__confirm-action__buttons"
                  @cancel="closeConfirmAction"
                  @save="onClearChatHistory()"
                >
                  <template #save> Clear History </template>
                </ControlButtons>
              </div>
              <template v-else>
                <div
                  ref="chatListRef"
                  class="modal-ai-chat__messages custom-scroll"
                  @scroll="onChatScroll"
                >
                  <Spinner
                    v-if="isLoading || isSending"
                    :loading="true"
                    :size="32"
                    class="modal-ai-chat__spinner"
                  />
                  <div
                    v-if="!isLoading"
                    class="modal-ai-chat__messages-inner"
                  >
                    <div
                      v-for="item in chatMessages"
                      :key="item.id"
                      class="modal-ai-chat__message-wrapper"
                      :class="{ 'modal-ai-chat__message-wrapper--mine': item.role === 'USER' }"
                    >
                      <div
                        v-if="item.role === 'USER' && item.content && item.notSent"
                        class="modal-ai-chat__resent-slot"
                      >
                        <VDropdown
                          theme="info-tooltip"
                          placement="bottom"
                          :distance="8"
                          :triggers="['click']"
                          :hideTriggers="['click']"
                          :autoHide="true"
                          popperClass="modal-ai-chat__resent-popper"
                          :container="pipContainer || 'body'"
                          class="modal-ai-chat__resent-dropdown"
                        >
                          <button
                            type="button"
                            class="modal-ai-chat__resent-button"
                          >
                            <icon
                              data="@icon/alert-tooltip.svg"
                              :fill="true"
                              width="var(--icon-16)"
                              height="var(--icon-16)"
                              class="modal-ai-chat__resent-icon"
                            />
                          </button>

                          <template #popper>
                            <p
                              v-if="item.errorMessage"
                              class="modal-ai-chat__resent-error"
                            >
                              {{ item.errorMessage }}
                            </p>
                            <button
                              v-close-popper.all
                              type="button"
                              class="link is-large modal-ai-chat__resent-action"
                              @click="onResendMessage(item)"
                            >
                              Resend
                            </button>
                          </template>
                        </VDropdown>
                        <span class="modal-ai-chat__resent-label">Not sent</span>
                      </div>
                      <div class="modal-ai-chat__message-content">
                        <AiChatAttachments
                          v-if="item.attachments.length || item.files.length"
                          :attachments="item.attachments"
                          :files="item.files"
                          :removable="false"
                          display="images"
                          class="modal-ai-chat__sent-images"
                        />
                        <div
                          v-if="item.content || item.attachments.length || item.files.length"
                          class="modal-ai-chat__message-bubble"
                        >
                          <AIResponseText
                            v-if="item.content"
                            :content="item.content"
                            class="modal-ai-chat__message"
                          />
                          <AiChatAttachments
                            v-if="item.attachments.length || item.files.length"
                            :attachments="item.attachments"
                            :files="item.files"
                            :removable="false"
                            display="documents"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="modal-ai-chat__actions">
                  <VDropdownWithKeyboardNavigation
                    id="ai-chat-menu"
                    className="modal-ai-chat__menu"
                    placement="bottom-end"
                    :distance="12"
                    :values="chatMenuOptions"
                    :selectedElementIndex="selectedMenuIndex"
                    popperClass="modal-ai-chat__menu-popper"
                    :container="pipContainer || 'body'"
                    :disabled="isLoading"
                    @selectedIndexUpdate="selectedMenuIndex = $event"
                    @onSelect="onMenuSelect"
                    @onShow="onDropdownVisibilityChange(true)"
                    @onHide="onDropdownVisibilityChange(false)"
                  >
                    <template #label>
                      <button
                        type="button"
                        class="modal-ai-chat__menu-button"
                      >
                        <icon
                          data="@icon/more-vertical.svg"
                          :fill="false"
                          width="var(--icon-24)"
                          height="var(--icon-24)"
                          class="modal-ai-chat__menu-icon"
                          :class="{ 'is-open': openDropdown }"
                        />
                      </button>
                    </template>
                    <template #item="{ value }">
                      <BaseSubHeading
                        size="large"
                        weight="400"
                        class="modal-ai-chat__menu-item"
                      >
                        {{ value.label }}
                      </BaseSubHeading>
                    </template>
                  </VDropdownWithKeyboardNavigation>
                  <ScrollToButton
                    class="modal-ai-chat__scroll-button"
                    :class="{ 'is-show': isScrollToBottomButtonVisible }"
                    @onClick="scrollToBottom"
                  />
                </div>
              </template>
            </div>

            <div
              class="modal-ai-chat__input-area"
              @paste="onPasteFiles"
              @dragover="onFileDragOver"
              @drop="onDropFiles"
            >
              <div
                class="modal-ai-chat__textarea-wrapper"
                :class="{ 'modal-ai-chat__textarea-wrapper--attachments': selectedFiles.length }"
              >
                <AiChatAttachments
                  :files="selectedFiles"
                  display="images"
                  :disabled="isSending"
                  @remove="removeSelectedFile"
                />
                <textarea
                  ref="inputRef"
                  v-model="prompt"
                  :disabled="isSending"
                  rows="3"
                  placeholder="Ask anything"
                  class="modal-ai-chat__textarea custom-scroll"
                  @keydown.enter.prevent="onSend"
                  @input="resetSendIdentity"
                />
              </div>
              <AiChatAttachments
                :files="selectedFiles"
                display="documents"
                class="custom-scroll"
                :disabled="isSending"
                @remove="removeSelectedFile"
              />
              <p
                v-if="attachmentError"
                role="alert"
                class="modal-ai-chat__attachment-error"
              >
                {{ attachmentError }}
              </p>
              <input
                ref="fileInputRef"
                type="file"
                multiple
                class="modal-ai-chat__file-input"
                aria-label="Add files to your AI message"
                :disabled="isSending"
                @change="onSelectFiles"
              />
              <div class="modal-ai-chat__action-bar">
                <button
                  type="button"
                  class="common-gray-interactive modal-ai-chat__add-file"
                  :disabled="isSending"
                  @click="fileInputRef?.click()"
                >
                  <icon
                    data="@icon/attach.svg"
                    :fill="false"
                    width="var(--icon-24)"
                    height="var(--icon-24)"
                  />
                  Add File
                </button>
                <button
                  type="button"
                  class="link is-large modal-ai-chat__send-btn"
                  @click="onSend"
                >
                  Send
                  <icon
                    data="@icon/paperplane.svg"
                    :fill="true"
                    width="var(--icon-24)"
                    height="var(--icon-24)"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Teleport>
    </template>
  </ModalAiChatWindow>
</template>

<script setup lang="ts">
/** Owns the AI conversation, including upload and retry state for attachment drafts. */
import { useRoute } from "vue-router"
import { v4 as uuidv4 } from "uuid"
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue"
import { useStore } from "vuex"

import AiChatAttachments from "@/components/AiSetup/AiChatAttachments.vue"
import AIResponseAgent from "@/components/AiSetup/AIResponseAgent.vue"
import BaseHeading from "@/components/Base/BaseHeading.vue"
import ScrollToButton from "@/components/Comment/ScrollToButton.vue"
import ControlButtons from "@/components/ControlButtons/index.vue"
import VDropdownWithKeyboardNavigation from "@/components/Dropdown/VDropdownWithKeyboardNavigation.vue"
import ModalAiChatWindow from "@/components/Modal/ModalAiChatWindow.vue"
import Spinner from "@/components/Spinner/index.vue"

import { useDocumentPictureInPicture } from "@/composables/useDocumentPictureInPicture"
import { AiChatMode, AiConversationMessage, AiConversations, AIProfile } from "@/models"
import { useWait } from "@/plugins/vuewait"
import { Actions, Getters } from "@/store"
import {
  AiSuggestionKey,
  closeFancyboxInWindow,
  getSuggestionsForAi,
  getValueByVariableName,
  openFancyboxInWindow
} from "@/utils"

import AIResponseText from "../AiSetup/AIResponseText.vue"
import BaseSubHeading from "../Base/BaseSubHeading.vue"

type ConfirmAction = "resetContext" | "clearChatHistory" | null
type ChatMessageView = AiConversationMessage & { files: File[] }

defineProps({
  id: {
    type: String,
    default: "modal-ai-chat"
  }
})
const emit = defineEmits(["opened", "closed"])

const store = useStore()
const route = useRoute()
const {
  isSupported: isPipSupported,
  container: pipContainer,
  isOpening: isPipOpening,
  open: openPipWindow,
  close: closePip
} = useDocumentPictureInPicture()

const prompt = ref("")
const minWindowSize = { width: 300, height: 400 }
const isOpen = ref(false)
const isClosePending = ref(false)
let isReloadPending = false
let isUnmounted = false
let pipGallery = null
const chatMode = computed<AiChatMode>({
  get: () => store.getters[Getters.GET_AI_CHAT_MODE],
  set: mode => store.dispatch(Actions.SET_AI_CHAT_MODE, mode)
})
const isInPip = computed<boolean>({
  get: () => store.getters[Getters.GET_AI_CHAT_IN_PIP],
  set: isPip => store.dispatch(Actions.SET_AI_CHAT_IN_PIP, isPip)
})
const isExpanded = computed(() => chatMode.value === "expanded")
const isSidebarOpen = ref(false)
const chatWindowRef = ref<InstanceType<typeof ModalAiChatWindow> | null>(null)
const openDropdown = ref(false)
const selectedMenuIndex = ref(-1)
const chatListRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLTextAreaElement | null>(null)
const isLoading = ref(false)
const wait = useWait()
const isSending = computed(() => wait.is("aiChat.send"))
const fileInputRef = ref<HTMLInputElement | null>(null)
const selectedFiles = ref<File[]>([])
const uploadedDrafts = new Map<File, string>()
const clientMessageId = ref("")
const attachmentError = ref("")
const pendingSend = ref<{ message: AiConversationMessage; files: File[] } | null>(null)
const isScrollToBottomButtonVisible = ref(false)
const chatMenuOptions = [
  { id: "resetContext", label: "Reset Context" },
  { id: "clearChatHistory", label: "Clear Chat History" }
]
const aiProviders = {
  OPEN_AI: { name: "OpenAI", url: "https://openai.com/" },
  GEMINI: { name: "Gemini", url: "https://gemini.google.com/" },
  GROK: { name: "Grok", url: "https://grok.com/" },
  DEEPSEEK: { name: "DeepSeek", url: "https://www.deepseek.com/" },
  CLAUDE: { name: "Claude", url: "https://claude.ai/" }
}
const activeConfirmAction = ref<ConfirmAction>(null)

const conversationParams = computed(() => store.getters[Getters.GET_CONVERSATION_PARAMS])

const suggestedQuestions = computed(() =>
  getSuggestionsForAi(route.meta.aiSuggestionKey as AiSuggestionKey | undefined)
)
const aiProfile = computed((): AIProfile => {
  return store.getters[Getters.GET_AI_PROFILE]
})
const aiProvider = computed(() => aiProviders[aiProfile.value.provider])

const aiConversation = computed<AiConversations>(() => store.getters[Getters.GET_AI_CONVERSATION])

/** Keeps local files beside the optimistic message and replaces the store's pending text row. */
const chatMessages = computed<ChatMessageView[]>(() => {
  const pending = pendingSend.value
  if (!pending) {
    return aiConversation.value.messages.map(message => ({ ...message, files: [] }))
  }
  return [
    ...aiConversation.value.messages
      .filter(message => message.id !== pending.message.id)
      .map(message => ({ ...message, files: [] })),
    { ...pending.message, files: pending.files }
  ]
})

/** Focuses the prompt after the modal has finished opening and its input is mounted. */
function focusInput() {
  nextTick(() => inputRef.value?.focus())
}

function onDropdownVisibilityChange(isOpen: boolean) {
  openDropdown.value = isOpen
  selectedMenuIndex.value = -1
}

function onSelectQuestion(question: string) {
  if (isSending.value || isLoading.value) return
  prompt.value = question
  resetSendIdentity()
  onSend()
}

function resetSendIdentity() {
  clientMessageId.value = ""
}

/** Moves the draft into chat immediately and restores it if upload or AI completion fails; an account switch mid-send stops it so the draft never reaches the new account. */
async function onSend() {
  const value = prompt.value.trim()
  if ((!value && !selectedFiles.value.length) || isSending.value || isLoading.value) return
  if (
    (await store.dispatch(Actions.HAS_FILE_OVER_SIZE_LIMIT, { files: selectedFiles.value })) ||
    isUnmounted
  )
    return
  activeConfirmAction.value = null
  wait.start("aiChat.send")
  if (!clientMessageId.value) clientMessageId.value = uuidv4()
  const files = [...selectedFiles.value]
  const messageId = clientMessageId.value
  const conversationId = aiConversation.value.id
  const context = {
    projectId: store.getters[Getters.GET_SESSION_PROJECT_ID],
    teamIds: store.getters[Getters.GET_AI_CONTEXT_TEAM_IDS],
    itemId: store.getters[Getters.GET_SESSION_ITEM_ID],
    ideaId: store.getters[Getters.GET_IDEA]?.id,
    sprintId: store.getters[Getters.GET_SESSION_SPRINT_ID],
    retrospectiveId: store.getters[Getters.GET_RETROSPECTIVE]?.id,
    epicId: store.getters[Getters.GET_EPIC]?.id
  }
  pendingSend.value = {
    message: new AiConversationMessage({ id: messageId, role: "USER", content: value }),
    files
  }
  prompt.value = ""
  selectedFiles.value = []
  attachmentError.value = ""
  let sent = false
  scrollToBottom()
  try {
    const pendingFiles = files.filter(file => !uploadedDrafts.has(file))
    if (pendingFiles.length) {
      const uploaded = await store.dispatch(Actions.ATTACHMENT_UPLOAD_TARGET, {
        attachments: pendingFiles,
        resourceType: "AI_CONVERSATION",
        targetId: conversationId,
        projectScoped: false
      })
      if (isUnmounted) return
      if (!Array.isArray(uploaded) || uploaded.length !== pendingFiles.length) {
        attachmentError.value =
          "Files could not be uploaded. Your message and files are ready to retry."
        return
      }
      pendingFiles.forEach((file, index) => uploadedDrafts.set(file, uploaded[index].id))
    }
    const result = await store.dispatch(Actions.POST_AI_CONVERSATION_MESSAGE, {
      conversationId,
      message: value,
      clientMessageId: clientMessageId.value,
      attachmentIds: files.map(file => uploadedDrafts.get(file)!),
      context
    })
    if (result) {
      sent = true
      uploadedDrafts.clear()
      clientMessageId.value = ""
      attachmentError.value = ""
    } else {
      attachmentError.value = files.length
        ? "Message could not be sent. Your files are ready to retry."
        : "Message could not be sent. Please retry."
    }
  } catch {
    attachmentError.value = files.length
      ? "Message could not be sent. Your files are ready to retry."
      : "Message could not be sent. Please retry."
  } finally {
    if (!isUnmounted) {
      if (!sent) {
        await store.dispatch(Actions.REMOVE_CURRENT_AI_CONVERSATION_MESSAGE, messageId)
        prompt.value = value
        selectedFiles.value = files
      }
      pendingSend.value = null
      wait.end("aiChat.send")
    }
  }
}

function onResendMessage(message: AiConversationMessage) {
  if (isSending.value) return
  if (message.attachments.length && !selectedFiles.value.length) return
  prompt.value = message.content
  clientMessageId.value = message.id
  onSend()
}

function scrollToBottom() {
  if (!chatListRef.value) return
  isScrollToBottomButtonVisible.value = false
  nextTick(() => {
    chatListRef.value?.scrollTo({
      top: 0,
      behavior: "smooth"
    })
  })
}

function onChatScroll() {
  if (!chatListRef.value) return
  isScrollToBottomButtonVisible.value =
    -chatListRef.value.offsetHeight / 2 > chatListRef.value.scrollTop
}

/** Restores the mode the chat was closed in; PiP can reopen here because the footer click is still a user gesture. */
function onOpened() {
  emit("opened")
  isOpen.value = true
  syncSidebar()
  if (isInPip.value) openPip()
  loadConversation()
}

/** Drafts uploaded to the previous conversation are discarded so the next send re-uploads them to the current one. */
async function loadConversation() {
  if (isLoading.value) {
    isReloadPending = true
    return
  }
  if (
    aiConversation.value.id &&
    aiConversation.value.accountId === store.getters[Getters.GET_ACCOUNT_ID] &&
    (aiConversation.value.contextEntityId || "") ===
      (conversationParams.value.contextEntityId || "") &&
    conversationParams.value.contextType === aiConversation.value.contextType
  ) {
    return
  }
  activeConfirmAction.value = null
  discardUploadedDrafts()
  isLoading.value = true
  try {
    await store.dispatch(Actions.GET_AI_CONVERSATION, {
      conversationParams: conversationParams.value
    })
  } finally {
    isLoading.value = false
  }
}

watch([conversationParams, isSending], () => {
  if (isOpen.value && !isSending.value) loadConversation()
})

/** Replays a context reload queued while any loading ran, including Reset Context and Clear Chat History. */
watch(isLoading, loading => {
  if (loading || !isReloadPending || !isOpen.value || isSending.value) return
  isReloadPending = false
  loadConversation()
})

async function onMenuSelect({ value }: { value: { id: string } }) {
  activeConfirmAction.value = value.id as Exclude<ConfirmAction, null>
  isScrollToBottomButtonVisible.value = false
}

function closeConfirmAction() {
  activeConfirmAction.value = null
}

/** Allowed while an answer is still processing: the server keeps that exchange and only breaks the context before it. */
async function onResetContext() {
  if (isLoading.value || !aiConversation.value.id) return
  isLoading.value = true
  activeConfirmAction.value = null
  try {
    await store.dispatch(Actions.CLEAR_AI_CONVERSATION_CONTEXT, {
      conversationId: aiConversation.value.id
    })
  } finally {
    isLoading.value = false
  }
}

/** Allowed while an answer is still processing: the server clears the finished exchanges and keeps the one in flight. */
async function onClearChatHistory() {
  if (isLoading.value || !aiConversation.value.id) return
  isLoading.value = true
  activeConfirmAction.value = null
  try {
    await store.dispatch(Actions.DELETE_AI_CONVERSATION_MESSAGES, {
      conversationId: aiConversation.value.id
    })
  } finally {
    isLoading.value = false
  }
}

function onSelectFiles(event: Event) {
  const input = event.target as HTMLInputElement
  addFiles(Array.from(input.files || []))
  input.value = ""
}

/** Keeps normal text pasting native while sending clipboard files through the picker policy. */
function onPasteFiles(event: ClipboardEvent) {
  const files = Array.from(event.clipboardData?.files || [])
  if (!files.length) return
  event.preventDefault()
  addFiles(files)
}

/** Accepts file drags without intercepting native text drag behavior. */
function onFileDragOver(event: DragEvent) {
  if (!event.dataTransfer?.types.includes("Files")) return
  event.preventDefault()
  event.dataTransfer.dropEffect = isSending.value ? "none" : "copy"
}

/** Prevents browser navigation for file drops, including while a send is in progress. */
function onDropFiles(event: DragEvent) {
  if (!event.dataTransfer?.types.includes("Files")) return
  event.preventDefault()
  addFiles(Array.from(event.dataTransfer.files))
}

/** Drops empty picker, clipboard, and dropped files; files over the plan's limit stay in the draft and block sending. Every file is re-created in this window, so files from the PiP window pass its Blob checks. */
function addFiles(selected: File[]) {
  if (isSending.value) return
  const nonemptyFiles = selected.filter(file => file.size > 0)
  attachmentError.value =
    nonemptyFiles.length !== selected.length ? "Empty files cannot be attached." : ""
  const inferredTypes: Record<string, string> = {
    csv: "text/csv",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    htm: "text/html",
    html: "text/html",
    json: "application/json",
    md: "text/markdown",
    txt: "text/plain",
    xml: "text/xml"
  }
  const files = nonemptyFiles.map(file => {
    const type =
      file.type && file.type !== "application/octet-stream"
        ? file.type
        : inferredTypes[file.name.split(".").pop()?.toLowerCase() || ""]
    return new File([file], file.name, {
      type: type || "application/octet-stream",
      lastModified: file.lastModified
    })
  })
  selectedFiles.value.push(...files)
  if (files.length) resetSendIdentity()
}

function removeSelectedFile(file: File) {
  selectedFiles.value = selectedFiles.value.filter(selected => selected !== file)
  const fileMetadataId = uploadedDrafts.get(file)
  if (fileMetadataId) {
    uploadedDrafts.delete(file)
    store.dispatch(Actions.DELETE_FILE_BY_ACCOUNT, { fileMetadataId })
  }
  resetSendIdentity()
  attachmentError.value = ""
}

/** A send still running after an account switch must not touch the remounted chat's shared sending state. */
onBeforeUnmount(() => {
  isUnmounted = true
  wait.end("aiChat.send")
  closeFancyboxInWindow(pipGallery)
  emit("closed")
})

function discardUploadedDrafts() {
  for (const fileMetadataId of uploadedDrafts.values()) {
    store.dispatch(Actions.DELETE_FILE_BY_ACCOUNT, { fileMetadataId })
  }
  uploadedDrafts.clear()
  clientMessageId.value = ""
}

function resetData() {
  discardUploadedDrafts()
  prompt.value = ""
  selectedFiles.value = []
  attachmentError.value = ""
  openDropdown.value = false
  selectedMenuIndex.value = -1
  isLoading.value = false
  wait.end("aiChat.send")
  isScrollToBottomButtonVisible.value = false
  activeConfirmAction.value = null
}

/** The compact and PiP windows start with the sidebar collapsed, so it opens as an overlay instead of squeezing the chat. */
function syncSidebar() {
  isSidebarOpen.value = isExpanded.value && !pipContainer.value
}

/** A manual resize and drag are dropped so the window returns to the position of the new mode. */
function onToggleSize() {
  chatMode.value = isExpanded.value ? "compact" : "expanded"
  syncSidebar()
  chatWindowRef.value?.resetLayout()
}

/** Opens the PiP window at the compact size of the current breakpoint; falls back to the in-page window if the browser refuses it. */
async function openPip() {
  isInPip.value = true
  const isOpened = await openPipWindow({
    width: parseFloat(getValueByVariableName({ variableName: "--ai-chat-compact-width" })) || 0,
    height: parseFloat(getValueByVariableName({ variableName: "--ai-chat-compact-height" })) || 0,
    className: "custom-modal custom-modal_no-padding modal-ai-chat modal-ai-chat--pip",
    minWidth: minWindowSize.width,
    minHeight: minWindowSize.height
  })
  if (!isOpened) isInPip.value = false
}

/** Returns the chat to the page in the size it was popped out from. */
function closePipToPage() {
  isInPip.value = false
  closePip()
}

/**
 * Image previews open inside the PiP window and go away with it. The pop-in button returns the chat to the page;
 * X closes the chat and keeps PiP to reopen in.
 */
watch(pipContainer, container => {
  syncSidebar()
  if (container) {
    container.addEventListener("click", event => {
      pipGallery = openFancyboxInWindow(event, container.ownerDocument.body) ?? pipGallery
    })
    return
  }
  closeFancyboxInWindow(pipGallery)
  pipGallery = null
  if (!isOpen.value || !isInPip.value) return
  if (isSending.value) {
    isClosePending.value = true
    return
  }
  chatWindowRef.value?.hide()
})

/** A PiP window closed mid-send closes the chat once the send settles, keeping it hidden meanwhile. */
watch(isSending, sending => {
  if (sending || !isClosePending.value) return
  isClosePending.value = false
  chatWindowRef.value?.hide()
})

function onClosed() {
  emit("closed")
  isOpen.value = false
  isClosePending.value = false
  closePip()
  if (isSending.value) return
  resetData()
}
</script>
